export type EmailQaState = "queued" | "running" | "passed" | "failed" | "blocked" | "cancelled";
export type EmailQaVerdict = "PASS" | "FAIL" | "BLOCKED";
export type EmailQaCategory = "continuity" | "questions" | "consistency" | "groundedness" | "clarity" | "termination";
export interface EmailQaModelRole { model: string; reasoning_effort: string; prompt_version: string; instructions: string | null }
export interface EmailQaStatus {
  available: boolean; reason: string | null;
  tenant: { id: string; name: string } | null; mailbox_address: string | null;
  models: Record<"scenario" | "reply" | "review", EmailQaModelRole>;
  limits: { batch: number; platform_replies: number; default_budget_micro_usd: number; max_budget_micro_usd: number };
  ai_quota: { usage: number; limit: number } | null;
}
export interface EmailQaRun {
  id: string; customer_number: number; customer_address: string; tenant_id: string; request_id: string | null;
  batch_id: string; batch_index: number; batch_size: number; state: EmailQaState; stage: string;
  title: string | null; budget_micro_usd: number; used_micro_usd: number; usage_uncertain: boolean;
  cancel_requested: boolean; created_at: string; started_at: string | null; finished_at: string | null;
  updated_at: string; terminal_reason: string | null; review_summary: string | null; platform_replies: number;
}
export interface EmailQaMessage { id: string; direction: "inbound" | "outbound"; subject: string; text: string }
export interface EmailQaDetail extends EmailQaRun {
  language: "de" | "en"; mailbox_address: string; origin_message_id: string | null;
  data: {
    scenario: { customer_name: string; title: string; profile: string; subject: string; body_text: string } | null;
    customer_messages: Array<{ rfc_message_id: string; ingested_message_id: string | null; subject: string;
      text: string; smtp_state: "intent" | "accepted" | "unknown" }>;
    turns: Array<{ index: number; snapshot: Record<string, unknown>; send_state: string;
      platform_message_id: string | null; receipt: { rfc_message_id: string; subject: string; text: string } | null }>;
    calls: Array<{ id: string; kind: string; state: string; model: string; reasoning_effort: string;
      prompt_version: string; started_at: string; reserved_micro_usd: number; input_tokens?: number;
      output_tokens?: number; estimated_cost_usd?: number; response_id?: string; error_code?: string }>;
    checks: Array<{ code: string; verdict: EmailQaVerdict; message_id?: string }>;
    terminal: Record<string, unknown> | null; terminal_reason: string | null; source_hash: string | null;
    review_input: { messages: EmailQaMessage[] } | null;
    review: { source_hash: string; verdict: EmailQaVerdict; summary: string;
      categories: Record<EmailQaCategory, { verdict: EmailQaVerdict; note: string }>;
      findings: Array<{ category: EmailQaCategory; severity: "error" | "warning"; message_id: string;
        quote: string; explanation: string; expected: string }> } | null;
  };
}
export interface EmailQaBatchInput { batch_id: string; count: number; budget_micro_usd: number; language: "de" | "en" }
export interface EmailQaClient {
  getEmailQaStatus(): Promise<EmailQaStatus>;
  listEmailQaRuns(tenantId: string): Promise<{ runs: EmailQaRun[] }>;
  getEmailQaRun(tenantId: string, runId: string): Promise<EmailQaDetail>;
  startEmailQaBatch(tenantId: string, input: EmailQaBatchInput): Promise<{ runs: EmailQaRun[] }>;
  cancelEmailQaRun(tenantId: string, runId: string): Promise<EmailQaDetail>;
}
export function emailQaReportUrl(tenantId: string, runId: string, format: "json" | "md"): string {
  return `/api/owner/email-qa/tenants/${encodeURIComponent(tenantId)}/runs/${encodeURIComponent(runId)}/report?format=${format}`;
}
